import { Field, useFormikContext } from "formik";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { List } from "react-window";
import DeleteIcon from "../../../assets/icons/delete-icon";
import SearchAddFeild from "../../forms/search-add-field";
import {
  purchaseProductDetailsAction,
  purchaseProductListAction,
} from "../../../redux/actions/purchase-action";
import { customerListAction } from "../../../redux/actions/general-action";
import CloseIcon from "../../../assets/icons/close-icon";
import SelectField from "../../forms/select-field";
import {
  buildUomPackDescription,
  convertBaseQtyToUom,
  getRowAllocations,
  getUomBaseFactor,
} from "../../../utils/purchase-order-calculations";

// Column order here must match the property order of each row object built
// in bulk-purchase-order.js's initialFormValues — cell rendering below picks
// cells by position (Object.keys/values(rowData)[colIndex]), not by name, so
// header labels and row data stay aligned only as long as both keep the same
// order.
// Widths are generous on purpose: unlike a plain <table>, CSS grid tracks
// don't auto-grow to fit content, so a column sized too tight for its real
// data (7-digit min/max qty, vendor codes/names) bleeds into its neighbor
// instead of wrapping. overflow-hidden + text-ellipsis below is the backstop
// for whatever still doesn't fit.
const COLUMNS = [
  { label: "S No.", width: "2rem" },
  { label: "Product Name", width: "minmax(10rem,1fr)" },
  { label: "Item Code", width: "4rem" },
  { label: "Pack Size", width: "6.5rem" },
  { label: "Min Qty.", width: "5.5rem" },
  { label: "Max Qty.", width: "5.5rem" },
  { label: "Quantity", width: "5.5rem" },
  { label: "Vendor Code", width: "5rem" },
  { label: "Vendor Name", width: "8rem" },
  { label: "Unit Price", width: "5rem" },
  { label: "Total (₹)", width: "6rem" },
  { label: "", width: "7.5rem" },
];
const GRID_TEMPLATE_COLUMNS = COLUMNS.map((column) => column.width).join(" ");
const ROW_HEIGHT = 50;

const cellClass =
  "px-[8px] text-left text-[length:var(--create-table-row-font-size)] font-[var(--create-table-row-font-weight)] text-[var(--create-table-row-fg-color)] whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer";

const headerCellClass =
  "px-[8px] whitespace-nowrap overflow-hidden text-ellipsis text-left text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)]";

const Row = ({
  index,
  style,
  rows,
  disabled,
  values,
  setFieldValue,
  initialData,
  onAllocate,
  removeRow,
  fetchData,
  fetchVendorData,
  handleQuantityChange,
  handlePriceChange,
  onSuccessDetails,
  onErrorDetails,
  dispatch,
  localData,
  inputRefs,
  quantityBeforeEditRef,
}) => {
  const rowData = rows[index];

  return (
    <div
      role="row"
      style={{ ...style, gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
      className="grid items-center bg-[var(--create-table-row-bg)] border-b border-[var(--create-table-border)]"
    >
      {Object?.values(rowData)?.map((cellData, colIndex) => {
        if (colIndex < 11) {
          return (
            <div role="cell" key={colIndex} className={cellClass}>
              {colIndex === 0 ? (
                <div>
                  <input
                    type="radio"
                    name="SerialNumberRadioGroup"
                    disabled={disabled}
                    value={rowData?.formikIndex}
                    className="peer hidden"
                    id={rowData?.formikIndex}
                  />
                  <label
                    htmlFor={rowData?.formikIndex}
                    className="block cursor-pointer select-none rounded-xl p-3 text-center hover:bg-slate-100 peer-checked:bg-blue-100 peer-checked:font-bold peer-checked:outline-none peer-checked:ring-2 peer-checked:ring-blue-500"
                  >
                    {rowData?.formikIndex + 1}
                  </label>
                </div>
              ) : colIndex === 1 || colIndex === 2 ? (
                <div>
                  <SearchAddFeild
                    isDisabled={
                      values?.data?.length - 1 !== rowData?.formikIndex ||
                      disabled
                        ? true
                        : false
                    }
                    errorClass="hidden"
                    isRequired={false}
                    value={{
                      label:
                        values?.data[rowData?.formikIndex][
                          Object.keys(rowData)[colIndex]
                        ],
                      value: "",
                    }}
                    name={`data[${rowData?.formikIndex}].${
                      Object.keys(rowData)[colIndex]
                    }`}
                    field={Object.keys(rowData)[colIndex]}
                    isLabelReq={false}
                    defaultSelectClass=""
                    placeholder=""
                    fetchData={fetchData}
                    onChange={async (selectedOption) => {
                      const filteredData = localData
                        ?.filter(
                          (item) => item?.item_id === selectedOption?.value
                        )
                        .map((item) => ({
                          sl_no: 1,
                          item_name: item?.item_name,
                          item_code: item?.item_code,
                          item_uom: item?.item_pur_purchasing_uom_name,
                          item_minimun_purchasing: 0,
                          item_maximum_purchasing: 0,
                          item_quantity: 0,
                          cmr_code: "",
                          cmr_first_name: "",
                          item_unit_price: item?.item_unit_price,
                          item_total_amount: 0,
                          item_id: item?.item_id,
                          item_generic_name: item?.item_generic_name,
                          item_manufacturer: item?.item_manufacturer,
                          item_group: item?.item_group,
                          item_type: item?.item_type,
                          item_categeory: item?.item_categeory,
                          item_uom_id: item?.item_pur_purchasing_uom_name_id,
                          uom_dropdown: [],
                          uom_group_id: item?.item_uom || "",
                        }));

                      dispatch(
                        purchaseProductDetailsAction(
                          {
                            item_id: filteredData?.[0]?.item_id,
                            item_uom: filteredData?.[0]?.uom_group_id,
                          },
                          (data) => {
                            onSuccessDetails(
                              data,
                              rowData?.formikIndex,
                              ...filteredData
                            );
                          },
                          onErrorDetails
                        )
                      );
                    }}
                  />
                </div>
              ) : colIndex === 7 || colIndex === 8 ? (
                (values?.data?.[rowData?.formikIndex]?.vendor_allocations
                  ?.length || 0) > 1 ? (
                  <span className="text-[var(--description-color)]">
                    {colIndex === 7
                      ? "Multiple"
                      : `${values.data[rowData.formikIndex].vendor_allocations.length} vendors`}
                  </span>
                ) : (
                  <div className="relative">
                    <SearchAddFeild
                      isDisabled={
                        values?.data?.length - 1 === rowData?.formikIndex ||
                        disabled
                          ? true
                          : false
                      }
                      errorClass="hidden"
                      isRequired={false}
                      value={{
                        label:
                          values?.data[rowData?.formikIndex][
                            Object.keys(rowData)[colIndex]
                          ],
                        value: "",
                      }}
                      name={`data[${rowData?.formikIndex}].${
                        Object.keys(rowData)[colIndex]
                      }`}
                      field={Object.keys(rowData)[colIndex]}
                      isLabelReq={false}
                      defaultSelectClass=""
                      placeholder=""
                      fetchData={fetchVendorData}
                      onChange={async (selectedOption) => {
                        setFieldValue(
                          `data[${rowData?.formikIndex}].cmr_first_name`,
                          selectedOption?.cmr_first_name
                        );
                        setFieldValue(
                          `data[${rowData?.formikIndex}].cmr_id`,
                          selectedOption?.value
                        );
                        setFieldValue(
                          `data[${rowData?.formikIndex}].cmr_code`,
                          selectedOption?.cmr_code
                        );
                        if (
                          (values?.data?.[rowData?.formikIndex]
                            ?.vendor_allocations?.length || 0) === 1
                        ) {
                          setFieldValue(
                            `data[${rowData?.formikIndex}].vendor_allocations[0].cmr_name`,
                            selectedOption?.cmr_first_name
                          );
                          setFieldValue(
                            `data[${rowData?.formikIndex}].vendor_allocations[0].cmr_id`,
                            selectedOption?.value
                          );
                          setFieldValue(
                            `data[${rowData?.formikIndex}].vendor_allocations[0].cmr_code`,
                            selectedOption?.cmr_code
                          );
                          setFieldValue(
                            `data[${rowData?.formikIndex}].vendor_allocations[0].cmr_ship_to_address`,
                            null
                          );
                        }
                      }}
                    />
                    {values?.data?.length - 1 !== rowData?.formikIndex &&
                      values?.data[rowData?.formikIndex][
                        Object.keys(rowData)[colIndex]
                      ] && (
                        <div
                          onClick={() => {
                            setFieldValue(
                              `data[${rowData?.formikIndex}].cmr_first_name`,
                              ""
                            );
                            setFieldValue(
                              `data[${rowData?.formikIndex}].cmr_id`,
                              ""
                            );
                            setFieldValue(
                              `data[${rowData?.formikIndex}].cmr_code`,
                              ""
                            );
                            if (
                              (values?.data?.[rowData?.formikIndex]
                                ?.vendor_allocations?.length || 0) === 1
                            ) {
                              setFieldValue(
                                `data[${rowData?.formikIndex}].vendor_allocations`,
                                []
                              );
                            }
                          }}
                          className="absolute cursor-pointer z-15 right-0 top-1/2 transform -translate-y-1/2"
                        >
                          <CloseIcon width={10} height={10} />
                        </div>
                      )}
                  </div>
                )
              ) : colIndex === 6 ? (
                <div>
                  <Field name={`data[${rowData?.formikIndex}].item_quantity`}>
                    {({ field }) => (
                      <input
                        min={0}
                        ref={inputRefs}
                        {...field}
                        type="number"
                        step="1"
                        placeholder=""
                        readOnly={
                          values?.data?.length - 1 === rowData?.formikIndex ||
                          disabled
                        }
                        onFocus={() => {
                          // Formik's default onChange (from the {...field}
                          // spread) live-updates item_quantity on every
                          // keystroke, so by blur time it already equals
                          // whatever was typed — capture the pre-edit value
                          // here instead, so handleQuantityChange can still
                          // tell whether this edit actually changed anything.
                          quantityBeforeEditRef.current =
                            values?.data?.[rowData?.formikIndex]?.item_quantity;
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.key === "-" ||
                            event.key === "e" ||
                            event.key === "."
                          ) {
                            event.preventDefault();
                          }
                          if (event.key === "Tab" || event.key === "Enter") {
                            handleQuantityChange(event, rowData?.formikIndex);
                            setFieldValue(
                              `data[${rowData?.formikIndex}].item_quantity`,
                              parseInt(event.target.value)
                            );
                          }
                        }}
                        onBlur={(event) => {
                          handleQuantityChange(event, rowData?.formikIndex);
                        }}
                        className="focus-within:ring-2 text-right min-w-[5rem]   px-2 w-full py-3 rounded focus-within:outline-none focus-within:shadow-[0px_0px_0px_4px_#EBF2FF input-field-no-spin-buttons"
                      />
                    )}
                  </Field>
                </div>
              ) : colIndex === 9 ? (
                <div>
                  <Field name={`data[${rowData?.formikIndex}].item_unit_price`}>
                    {({ field }) => (
                      <input
                        {...field}
                        type="number"
                        step="1"
                        placeholder=""
                        readOnly={
                          values?.data?.length - 1 === rowData?.formikIndex ||
                          disabled
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            handlePriceChange(event, rowData?.formikIndex);
                            setFieldValue(
                              `data[${rowData?.formikIndex}].item_unit_price`,
                              parseFloat(event.target.value)
                            );
                          }
                        }}
                        onBlur={(event) => {
                          handlePriceChange(event, rowData?.formikIndex);
                        }}
                        className="focus-within:ring-2 text-right min-w-[5rem]   px-2 w-full py-3 rounded focus-within:outline-none focus-within:shadow-[0px_0px_0px_4px_#EBF2FF input-field-no-spin-buttons"
                      />
                    )}
                  </Field>
                </div>
              ) : colIndex === 3 &&
                values?.data?.length - 1 !== rowData?.formikIndex ? (
                <SelectField
                  general_color={false}
                  handleSelected={(sel) => {
                    const rowIndex = rowData?.formikIndex;
                    const row = values?.data?.[rowIndex];
                    setFieldValue(
                      `data[${rowIndex}].item_uom_id`,
                      sel?.item_uom_id
                    );
                    // Retain the newly selected UOM's conversion factor on the
                    // row — the same field the regular PO screen writes on a
                    // UOM change, and the one every base-unit comparison reads.
                    const factor = getUomBaseFactor(sel);
                    setFieldValue(`data[${rowIndex}].selected_uom_qty`, factor);
                    if (sel) {
                      setFieldValue(
                        `data[${rowIndex}].item_baby_box_packing`,
                        buildUomPackDescription(sel)
                      );
                    }
                    // item_quantity is expressed in the SELECTED uom, so the
                    // UOM change has to re-derive it from the base-unit
                    // requirement kept on the row — otherwise switching e.g.
                    // Strip -> Box would keep ordering the strip count as
                    // boxes. Rows the user typed in by hand (no
                    // item_quantity_base) are left completely alone.
                    const baseQty = Number(row?.item_quantity_base) || 0;
                    if (baseQty <= 0) return;
                    const nextQty = convertBaseQtyToUom(baseQty, sel);
                    if (nextQty === Number(row?.item_quantity)) return;
                    setFieldValue(`data[${rowIndex}].item_quantity`, nextQty);
                    setFieldValue(
                      `data[${rowIndex}].item_total_amount`,
                      nextQty * (Number(row?.item_unit_price) || 0)
                    );
                    // handleSubmit sends each ALLOCATION's qty, so a stale
                    // allocation would silently ship the pre-conversion
                    // number. A single allocation is just resized (the
                    // prefilled vendor is kept); a real multi-vendor split
                    // can't be rebalanced automatically, so it is cleared the
                    // same way handleQuantityChange already clears it when
                    // the quantity changes.
                    const allocations = row?.vendor_allocations || [];
                    if (allocations.length === 1) {
                      setFieldValue(
                        `data[${rowIndex}].vendor_allocations[0].qty`,
                        nextQty
                      );
                    } else if (allocations.length > 1) {
                      setFieldValue(`data[${rowIndex}].vendor_allocations`, []);
                      setFieldValue(`data[${rowIndex}].cmr_id`, "");
                      setFieldValue(`data[${rowIndex}].cmr_code`, "");
                      setFieldValue(`data[${rowIndex}].cmr_first_name`, "");
                    }
                  }}
                  borderclass=""
                  defaultSelectClass="border-0"
                  selectClass="w-full"
                  defaultValue={
                    values.data[rowData?.formikIndex]?.item_uom
                      ? { label: values.data[rowData?.formikIndex]?.item_uom ?? "" }
                      : null
                  }
                  isDisabled={disabled}
                  errMsgContainerClass="hidden"
                  id={`data[${rowData?.formikIndex}].item_uom`}
                  isContainer={false}
                  options={values.data[rowData?.formikIndex]?.uom_dropdown}
                  name={`data[${rowData?.formikIndex}].item_uom`}
                  placeholder={"Select UOM"}
                />
              ) : (
                cellData
              )}
            </div>
          );
        }
        return null;
      })}
      <div role="cell" className={cellClass}>
        {!disabled && rowData?.formikIndex !== values?.data?.length - 1 && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onAllocate(rowData?.formikIndex)}
              className="bg-[var(--primary)] text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:opacity-90 whitespace-nowrap"
            >
              Allocate
            </button>
            <DeleteIcon onClickFn={() => removeRow(rowData?.formikIndex)} />
          </div>
        )}
      </div>
    </div>
  );
};

const BulkBillingTable = ({
  disabled = false,
  initialData = {},
  onAllocate = () => {},
}) => {
  const { setFieldValue, values } = useFormikContext();
  const inputRefs = useRef(null);
  const quantityBeforeEditRef = useRef(null);
  const dispatch = useDispatch();
  const [localData, setLocalData] = useState([]);

  const removeRow = (rowIndex) => {
    const updatedData = [...values.data];
    updatedData.splice(rowIndex, 1);
    setFieldValue("data", updatedData);
  };

  const fetchData = async (inputValue, callback, field) => {
    const onSuccess = (data) => {
      const options =
        data?.data === null
          ? []
          : data.data
              .map((item) => ({
                value: item.item_id,
                label: item[field],
              }))
              .filter(
                (option) =>
                  !values?.data?.some((item) => item?.item_id === option.value)
              );
      setLocalData(data.data);
      callback(options);
    };
    const onError = (data) => {};
    if (inputValue?.length > 0) {
      dispatch(
        purchaseProductListAction({ field: field, value: inputValue }, onSuccess, onError)
      );
    }
  };

  const fetchVendorData = async (inputValue, callback, field) => {
    const onSuccess = (data) => {
      const options =
        data?.data === null
          ? []
          : data.data.map((item) => ({
              value: item.cmr_id,
              label: item[field],
              cmr_first_name: item.cmr_first_name,
              cmr_code: item?.cmr_code,
            }));

      callback(options);
    };
    const onError = (data) => {};
    if (inputValue?.length > 0) {
      dispatch(
        customerListAction(
          { field: field, value: inputValue, isVendor: true },
          onSuccess,
          onError
        )
      );
    }
  };

  const handleQuantityChange = async (event, rowIndex) => {
    const quantity = parseInt(event.target.value);
    const row = values.data[rowIndex];
    const unitPrice = row.item_unit_price;
    const total = quantity * unitPrice;
    // Not row.item_quantity — Formik's default onChange (from the
    // {...field} spread on the input) already live-updated it to match
    // whatever was typed, so by now it's identical to `quantity`. The
    // pre-edit value is only available from the onFocus snapshot.
    const previousQuantity = Number(quantityBeforeEditRef.current) || 0;
    const wasAllocated = getRowAllocations(row).length > 0;
    setFieldValue(`data[${rowIndex}].item_quantity`, quantity);
    setFieldValue(`data[${rowIndex}].item_total_amount`, total);
    // Keep the base-unit requirement in step with what the user actually
    // typed — otherwise a later UOM change would reconvert from the stale
    // load-time suggestion and silently overwrite this edit. Rows with no
    // pack maths have selected_uom_qty 1, so this is a no-op for them.
    if (row?.item_quantity_base !== undefined) {
      setFieldValue(
        `data[${rowIndex}].item_quantity_base`,
        quantity * (Number(row?.selected_uom_qty) || 1)
      );
    }
    // Whatever vendor split existed was sized for the old quantity — once it
    // changes, that allocation no longer adds up, so clear it and make the
    // user re-allocate against the new total instead of leaving a stale,
    // unbalanced split in place.
    if (wasAllocated && quantity !== previousQuantity) {
      setFieldValue(`data[${rowIndex}].vendor_allocations`, []);
      setFieldValue(`data[${rowIndex}].cmr_id`, "");
      setFieldValue(`data[${rowIndex}].cmr_code`, "");
      setFieldValue(`data[${rowIndex}].cmr_first_name`, "");
    }
  };

  const handlePriceChange = async (event, rowIndex) => {
    const unitPrice = parseFloat(event.target.value);
    const quantity = values.data[rowIndex].item_quantity;
    const total = quantity * unitPrice;
    setFieldValue(`data[${rowIndex}].item_total_amount`, total);
  };

  const onSuccessDetails = (data, rowIndex, filteredData) => {
    const oldData = [...values?.data];
    oldData.pop();
    const updatedData = {
      ...filteredData,
      uom_dropdown: data?.data?.uom_dropdown
        ? data?.data?.uom_dropdown?.map((uom) => ({
            item_uom_id: uom?.item_id,
            label: uom?.alt_uom,
            value: uom?.alt_uom,
            // Same extra fields buildRowFromItem keeps, so a hand-searched
            // row's UOM dropdown also carries real pack sizes instead of
            // falling back to the pack's own alt_uom name.
            qty: uom?.base_quantity,
            base_quantity: uom?.base_quantity,
            alt_quantity: uom?.alt_quantity,
            pack_description: uom?.pack_description,
            base_uom_name: uom?.base_uom?.name || uom?.sub_uom || "",
          }))
        : [],
    };

    setFieldValue("data", [...oldData, updatedData, initialData]);
    inputRefs.current?.focus();
  };

  const onErrorDetails = (data) => {};

  const rows = BulkOrderfilterFn(values?.data, {});

  return (
    <div role="table" className="h-full flex flex-col">
      <div
        role="row"
        style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
        className="grid items-center flex-none h-[50px] bg-[var(--create-table-header-bg)] sticky top-0 z-[10] overflow-hidden"
      >
        {COLUMNS.map((column, index) => (
          <div key={index} role="columnheader" className={headerCellClass}>
            {column.label}
          </div>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <List
          rowComponent={Row}
          rowCount={rows.length}
          rowHeight={ROW_HEIGHT}
          rowProps={{
            rows,
            disabled,
            values,
            setFieldValue,
            initialData,
            onAllocate,
            removeRow,
            fetchData,
            fetchVendorData,
            handleQuantityChange,
            handlePriceChange,
            onSuccessDetails,
            onErrorDetails,
            dispatch,
            localData,
            inputRefs,
            quantityBeforeEditRef,
          }}
          style={{ height: "100%", width: "100%", overflowX: "hidden" }}
        />
      </div>
    </div>
  );
};

export default BulkBillingTable;

function groupBy(items, key) {
  return items.reduce((result, item) => {
    (result[item[key]] = result[item[key]] || []).push(item);
    return result;
  }, {});
}

export function BulkOrderfilterFn(items, filterData) {
  let filteredItems = items
    ?.map((item, index) => ({ ...item, formikIndex: index }))
    ?.filter(
      (item) =>
        (filterData?.vendor ? item?.cmr_id === filterData?.vendor : true) &&
        (filterData?.manufacturer
          ? item?.item_manufacturer === filterData?.manufacturer
          : true) &&
        (filterData?.item_group
          ? item?.item_group === filterData?.item_group
          : true) &&
        (filterData?.item_type
          ? item?.item_type === filterData?.item_type
          : true) &&
        (filterData?.item_category
          ? item?.item_category === filterData?.item_category
          : true)
    );

  if (filterData?.group_by) {
    const groupedItems = groupBy(filteredItems, filterData.group_by);
    filteredItems = Object.values(groupedItems).flat();
  }

  return filteredItems;
}
